/**
 * MetricCard - Premium metric card with count-up animation and glow effects
 *
 * Features:
 * - Animated number count-up on load/value change
 * - Subtle glow effect on hover
 * - Mini sparkline chart
 * - Smooth micro-interactions
 */

import React, { useEffect, useRef, useState } from "react";
import { motion, useSpring, useTransform, useInView } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

type ColorTheme = "brand" | "green" | "blue" | "purple" | "red" | "cyan";

interface MetricCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color: ColorTheme;
  data?: number[];
  tooltipFormatter?: (value: number) => string;
  index?: number; // For stagger animation
  percentChange?: number | null;
}

// Convert hex color to RGB components for use in rgba()
function hexToRgb(hex: string): string {
  const safeHex = hex ?? "";
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(safeHex);
  if (!result) return "99,102,241";
  return `${parseInt(result[1] ?? "0", 16)},${parseInt(result[2] ?? "0", 16)},${parseInt(result[3] ?? "0", 16)}`;
}

// Build color config with Urban Unity's scorecard accents.
function buildColorConfig(colors: { primary: string }): Record<ColorTheme, {
  text: string;
  stroke: string;
  rgb: string;
}> {
  const brandRgb = hexToRgb(colors.primary);

  return {
    brand: {
      text: "text-[var(--brand-primary)]",
      stroke: colors.primary,
      rgb: brandRgb,
    },
    green: {
      text: "text-emerald-500",
      stroke: "#10b981",
      rgb: "16,185,129",
    },
    blue: {
      text: "text-sky-400",
      stroke: "#3b82f6",
      rgb: "59,130,246",
    },
    purple: {
      text: "text-purple-400",
      stroke: "#8b5cf6",
      rgb: "139,92,246",
    },
    red: {
      text: "text-red-500",
      stroke: "#ef4444",
      rgb: "239,68,68",
    },
    cyan: {
      text: "text-cyan-400",
      stroke: "#22d3ee",
      rgb: "34,211,238",
    },
  };
}

// Parse number from formatted string (handles $, K, M, %, commas)
function parseDisplayValue(value: string | number): number {
  if (typeof value === "number") return value;
  const cleaned = value.replace(/[$,%KM]/g, "").replace(/,/g, "");
  let num = parseFloat(cleaned) || 0;
  if (value.includes("M")) num *= 1000000;
  if (value.includes("K")) num *= 1000;
  return num;
}

// Format number back to display format
function formatToDisplay(num: number, originalValue: string): string {
  if (originalValue.includes("M")) {
    return `$${(num / 1000000).toFixed(2)}M`;
  }
  if (originalValue.includes("K")) {
    return `$${(num / 1000).toFixed(1)}K`;
  }
  if (originalValue.includes("%")) {
    return `${num.toFixed(1)}%`;
  }
  if (originalValue.startsWith("$")) {
    return `$${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// Animated number component - only animates on initial load, not filter changes
function AnimatedNumber({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const targetValue = parseDisplayValue(value);
  const hasAnimatedOnce = useRef(false);

  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    duration: 1.2,
  });

  const display = useTransform(spring, (current) => formatToDisplay(current, value));
  const [displayValue, setDisplayValue] = useState(formatToDisplay(0, value));

  // Only animate the first time the component becomes visible
  useEffect(() => {
    if (isInView && !hasAnimatedOnce.current) {
      hasAnimatedOnce.current = true;
      spring.set(targetValue);
    }
  }, [isInView, targetValue, spring]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  // When value changes after initial animation, update instantly (no animation)
  useEffect(() => {
    if (hasAnimatedOnce.current) {
      // Jump directly to new value without animating
      spring.jump(targetValue);
    }
  }, [value, spring, targetValue]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}

// Default tooltip formatter
const defaultFormatter = (value: number) => value.toLocaleString();

function buildSparkline(values: number[], width: number, height: number) {
  if (values.length === 0 || width <= 0 || height <= 0) {
    return { points: "", areaPoints: "", lastX: width - 6, lastY: height / 2 };
  }

  const safeValues = values.map((value) => (Number.isFinite(value) ? value : 0));
  const min = Math.min(...safeValues);
  const max = Math.max(...safeValues);
  const range = max - min || 1;
  const topPad = 4;
  const bottomPad = 4;
  const usableHeight = Math.max(1, height - topPad - bottomPad);

  const points = safeValues.map((value, index) => {
    const x = safeValues.length > 1 ? (index / (safeValues.length - 1)) * width : width / 2;
    const normalized = (value - min) / range;
    const y = topPad + (1 - normalized) * usableHeight;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const lastPair = points[points.length - 1]?.split(",") ?? [`${width - 6}`, `${height / 2}`];
  const firstPair = points[0]?.split(",") ?? ["0", `${height / 2}`];

  return {
    points: points.join(" "),
    areaPoints: `${firstPair[0]},${height} ${points.join(" ")} ${lastPair[0]},${height}`,
    lastX: Number(lastPair[0]),
    lastY: Number(lastPair[1]),
  };
}

export function MetricCard({
  label,
  value,
  icon,
  color,
  data = [],
  tooltipFormatter = defaultFormatter,
  index = 0,
  percentChange,
}: MetricCardProps) {
  const { activeTheme, theme: themeMode } = useTheme();
  const isOrange = themeMode === "orange";
  const isDark = themeMode === "dark";
  const colorConfig = buildColorConfig(activeTheme.colors);
  const theme = colorConfig[color];
  const { rgb } = theme;
  const sparklineWidth = 112;
  const sparklineHeight = 56;
  const { points, areaPoints, lastX, lastY } = buildSparkline(data, sparklineWidth, sparklineHeight);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        boxShadow: `0 0 50px -5px rgba(${rgb}, 0.5)`,
        transition: { duration: 0.2 }
      }}
      className="relative overflow-hidden border rounded-2xl p-4 md:p-6 transition-shadow duration-500 ease-out backdrop-blur-sm"
      style={(isOrange ? {
        background: "rgba(80, 25, 5, 0.55)",
        backdropFilter: "blur(12px)",
        borderColor: `rgba(${rgb}, 0.45)`,
        boxShadow: `0 0 30px -5px rgba(${rgb}, 0.35)`,
      } : isDark ? {
        background: "rgba(14, 14, 14, 0.85)",
        borderColor: `rgba(${rgb}, 0.45)`,
        boxShadow: `0 0 45px -5px rgba(${rgb}, 0.55)`,
      } : {
        background: `linear-gradient(to bottom right, rgba(${rgb}, 0.2), rgba(${rgb}, 0.1), transparent)`,
        borderColor: `rgba(${rgb}, 0.2)`,
        boxShadow: `0 0 30px -5px rgba(${rgb}, 0.3)`,
      }) as React.CSSProperties}
    >
      {/* Subtle animated gradient overlay */}
      <motion.div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${theme.stroke}15, transparent 50%)`,
        }}
      />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative flex items-center justify-between gap-3">
        <div className="space-y-3 flex-1 min-w-0">
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.2 }}
          >
            {icon && (
              <motion.div
                className={`size-5 ${theme.text}`}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {icon}
              </motion.div>
            )}
            <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              {label}
            </span>
          </motion.div>
          <div className="flex items-baseline gap-2">
            <AnimatedNumber
              value={String(value)}
              className="text-2xl md:text-3xl font-bold text-foreground tracking-tight"
            />
            {percentChange != null && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 + 0.4 }}
                className={`text-sm font-semibold ${
                  percentChange > 0
                    ? "text-emerald-400"
                    : percentChange < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}
              >
                {percentChange > 0 ? "+" : ""}
                {percentChange.toFixed(2)}%
              </motion.span>
            )}
          </div>
        </div>

        {/* Mini Chart — clipped so it never bleeds over the number */}
        {data.length > 0 && points && (
          <motion.div
            className="hidden sm:block w-20 h-10 md:w-28 md:h-14 shrink-0 overflow-hidden"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 + 0.3, duration: 0.4 }}
          >
            <svg
              width={sparklineWidth}
              height={sparklineHeight}
              viewBox={`0 0 ${sparklineWidth} ${sparklineHeight}`}
              className="h-full w-full"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id={`spark-${label.replace(/\s+/g, "-").toLowerCase()}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.stroke} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={theme.stroke} stopOpacity="0.04" />
                </linearGradient>
              </defs>
              <polygon
                points={areaPoints}
                fill={`url(#spark-${label.replace(/\s+/g, "-").toLowerCase()})`}
              />
              <polyline
                points={points}
                fill="none"
                stroke={theme.stroke}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx={Math.max(6, Math.min(sparklineWidth - 6, lastX))}
                cy={Math.max(6, Math.min(sparklineHeight - 6, lastY))}
                r="3"
                fill={theme.stroke}
              />
            </svg>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// Skeleton version for loading state
export function MetricCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
      className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
    >
      <div className="flex items-center justify-between">
        <div className="space-y-3 flex-1">
          <div className="flex items-center gap-2">
            <div className="size-5 rounded bg-muted animate-pulse" />
            <div className="h-4 w-24 rounded bg-muted animate-pulse" />
          </div>
          <div className="h-8 w-32 rounded bg-muted animate-pulse" />
        </div>
        <div className="w-32 h-16 rounded bg-muted/50 animate-pulse" />
      </div>
      {/* Shimmer overlay */}
      <motion.div
        className="absolute inset-0 -translate-x-full"
        animate={{ translateX: ["100%", "-100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
        }}
      />
    </motion.div>
  );
}
